import { BeneficiaryAggregateMultiselectDimension } from '@repo/shared/dtos/beneficiary'
import { MultiselectSchemaItem } from '@repo/shared/dtos/structured-data'
import { Button } from '@repo/ui/button'
import { ColorPicker } from '@repo/ui/color-picker'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Tags } from '@repo/ui/tags'
import { Plus, Trash2 } from 'lucide-react'
import { useMemo } from 'react'
import { Fragment } from 'react/jsx-runtime'

export function MultiSelectBuckets({
  dimension,
  dimensionSchema,
  onChange,
}: {
  dimension: BeneficiaryAggregateMultiselectDimension
  dimensionSchema: MultiselectSchemaItem
  onChange: (dimension: BeneficiaryAggregateMultiselectDimension) => void
}) {
  const { t } = useT({ keyPrefix: 'admin.insights.form.dimensions.buckets.multiselect' })
  const buckets = useMemo(() => dimension.buckets, [dimension])

  const updateProperty = (
    index: number,
    property: 'label' | 'min' | 'max' | 'color',
    value: string,
  ) => {
    onChange({
      ...dimension,
      buckets: buckets.map((b, i) => (i === index ? { ...b, [property]: value } : b)),
    })
  }

  const addBucket = () => {
    onChange({
      ...dimension,
      buckets: [...buckets, { label: '', values: [] }],
    })
  }

  const deleteBucket = (index: number) => {
    onChange({
      ...dimension,
      buckets: buckets.filter((_, i) => i !== index),
    })
  }

  const handleSelection = (index: number, selectedItemIds: string[]) => {
    onChange({
      ...dimension,
      buckets: buckets.map((b, i) => (i === index ? { ...b, values: selectedItemIds } : b)),
    })
  }

  return (
    <div className="rounded-lg border border-primary-200 p-4 bg-gray-50 space-y-4">
      <div className="space-y-2">
        <div className="grid [grid-template-columns:2fr_3fr_3rem_3rem] gap-2">
          <div className="text-sm font-medium text-primary-600">{t('columns.label')}</div>
          <div className="text-sm font-medium text-primary-600">{t('columns.values')}</div>
          <div className="text-sm font-medium text-primary-600">{t('columns.color')}</div>
          <div></div>
          {buckets.map((bucket, index) => (
            <Fragment key={index}>
              <Input
                id={`label-${index}`}
                type="text"
                placeholder={t('labelPlaceholder')}
                value={bucket.label}
                onChange={(event) => updateProperty(index, 'label', event.target.value)}
              />
              <Tags
                items={dimensionSchema.enumValues.map((value) => ({ id: value, label: value }))}
                initialSelectedIds={bucket.values}
                onChange={(selectedItemIds) => handleSelection(index, selectedItemIds)}
                placeholder={t('selectPlaceholder')}
              />
              <ColorPicker
                color={bucket.color}
                onChange={(value) => updateProperty(index, 'color', value)}
              />
              <Button variant="outline" size="sm" onClick={() => deleteBucket(index)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </Fragment>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={() => addBucket()} className="w-full">
          <Plus className="w-4 h-4" />
          {t('addBucket')}
        </Button>
      </div>
    </div>
  )
}
